<script setup>
// O MEGAFONE DAS NOVIDADES (22/10/2026), ao lado do sino. Victor sugeriu (a Tramitação tem um): o sino é operacional —
// urgências, mensagens, andamentos — e novidade do produto ali dentro disputava atenção com trabalho. Ícone próprio é o
// padrão de quem faz bem (Beamer, Headway, Canny). O ponto íris aparece enquanto houver novidade que a pessoa ainda não
// viu, e o megafone dá uma sacudida curta ao carregar a tela nessa hora.
//
// O clique abre a LISTA, como o sino (Victor, 22/10/2026: "não quero que abra já direto as novidades… uma lista do que
// teve de novidade… apertando na novidade, vai rolar para onde está aquela novidade"). Cada item tem quando saiu e o
// ponto da que é nova; o toque leva à página já no capítulo dela (/novidades#retomada). Abrir a lista não marca nada
// como visto: quem marca é a visita à página.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useSidebarLayout } from 'dashboard/components-next/sidebar/provider';
import { DropdownContainer, DropdownBody } from 'next/dropdown-menu/base';
import { NOVIDADES, novasPara, quandoSaiu } from './catalogo';

const { t } = useI18n();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const { uiSettings } = useUISettings();
const { isHorizontal } = useSidebarLayout();

const novas = computed(() => novasPara(uiSettings.value || {}));
const idsNovas = computed(() => new Set(novas.value.map(n => n.id)));
const rotulo = computed(() =>
  novas.value.length
    ? t(
        'NOVIDADES.PAGINA.NO_SINO_NOVAS',
        { n: novas.value.length },
        novas.value.length
      )
    : t('NOVIDADES.PAGINA.NO_SINO')
);

const quando = n => {
  const { chave, data } = quandoSaiu(n.data);
  return t(`NOVIDADES.PAGINA.${chave}`, { data });
};

// O corpo do dropdown não entrega um "fechar": guardado quando abre (como no sino).
let fecharPainel = () => {};

const abrirOuFechar = (toggle, isOpen) => {
  if (!isOpen) fecharPainel = () => toggle();
  toggle();
};

const irPara = n => {
  fecharPainel();
  router.push({
    name: 'novidades_index',
    params: { accountId: accountId.value },
    ...(n ? { hash: `#${n.id}` } : {}),
  });
};
</script>

<template>
  <DropdownContainer class="relative flex-shrink-0">
    <template #trigger="{ toggle, isOpen }">
      <button
        type="button"
        class="megafone"
        :class="{ 'megafone--novo': novas.length, 'megafone--aberto': isOpen }"
        :title="rotulo"
        :aria-label="rotulo"
        :aria-expanded="isOpen"
        @click="abrirOuFechar(toggle, isOpen)"
      >
        <span class="megafone__ico i-ph-megaphone-simple" aria-hidden="true" />
        <span v-if="novas.length" class="megafone__ponto" aria-hidden="true" />
      </button>
    </template>

    <!-- `advup-tema-pagina`: nasce dentro do topo escuro; devolve as cores do tema da página (ver o sino). -->
    <DropdownBody
      class="advup-tema-pagina mgf z-50 p-0 overflow-hidden"
      :class="
        isHorizontal
          ? '!fixed top-[var(--advup-altura-topo)] right-4 left-4 sm:left-auto sm:w-[24rem] w-auto'
          : 'bottom-12 mb-2 ltr:left-0 rtl:right-0 w-[24rem]'
      "
    >
      <div class="mgf__topo">
        <span class="mgf__tit">
          <i class="i-ph-megaphone-simple-fill" aria-hidden="true" />
          {{ t('NOVIDADES.PAGINA.PAINEL_TIT') }}
          <span v-if="novas.length" class="mgf__conta">{{ novas.length }}</span>
        </span>
      </div>

      <ul class="mgf__lista">
        <li v-for="n in NOVIDADES" :key="n.id">
          <button
            type="button"
            class="mgf__item"
            :class="[
              `mgf--${n.cor}`,
              { 'mgf__item--nova': idsNovas.has(n.id) },
            ]"
            :data-novidade="n.id"
            @click="irPara(n)"
          >
            <span class="mgf__ico" aria-hidden="true">
              <i :class="n.icone" />
            </span>
            <span class="mgf__txt">
              <span class="mgf__nome">{{ t(`${n.textos}.TITULO`) }}</span>
              <span class="mgf__quando">
                {{ quando(n) }}
                <span aria-hidden="true">·</span>
                {{ t(`${n.textos}.SELO`) }}
              </span>
            </span>
            <span
              v-if="idsNovas.has(n.id)"
              class="mgf__ponto"
              :title="t('NOVIDADES.PAGINA.NOVA')"
            />
          </button>
        </li>
      </ul>

      <div class="mgf__rodape">
        <button type="button" class="mgf__ver" @click="irPara()">
          {{ t('NOVIDADES.PAGINA.VER_TODAS') }}
          <i class="i-ph-arrow-right" aria-hidden="true" />
        </button>
      </div>
    </DropdownBody>
  </DropdownContainer>
</template>

<style scoped>
.megafone {
  position: relative;
  display: grid;
  flex-shrink: 0;
  place-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  transition: background-color var(--mov-1, 0.15s) var(--mov-curva, ease);
}

.megafone:hover {
  background: rgb(var(--slate-12) / 0.08);
}

.megafone--aberto {
  background: rgb(var(--slate-12) / 0.12);
}

.megafone:focus-visible {
  outline: 2px solid #8b7bff;
  outline-offset: 2px;
}

.megafone__ico {
  width: 16px;
  height: 16px;
  color: rgb(var(--slate-11));
}

.megafone--novo .megafone__ico {
  color: #a99dff;
  animation: megafoneSacode 1.4s var(--mov-curva, ease) 0.6s 2;
}

/* O ponto íris: a cor da IA e das novidades (o mesmo da página). */
.megafone__ponto {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border: 2px solid #0f172a;
  border-radius: 50%;
  background: #8b7bff;
  animation: megafonePulsa 2.4s ease-out infinite;
}

@keyframes megafoneSacode {
  0%,
  60%,
  100% {
    transform: rotate(0);
  }

  10%,
  30% {
    transform: rotate(-14deg);
  }

  20%,
  40% {
    transform: rotate(10deg);
  }
}

@keyframes megafonePulsa {
  0% {
    box-shadow: 0 0 0 0 rgb(139 123 255 / 0.6);
  }

  70%,
  100% {
    box-shadow: 0 0 0 7px rgb(139 123 255 / 0);
  }
}

/* O painel: a lista, a mais nova em cima, com quando saiu. */
.mgf {
  --cor-iris: 109 93 252;
  --cor-ambar: 217 145 0;
  --cor-marca: 47 95 240;
}

.mgf--iris {
  --cap-cor: var(--cor-iris);
}

.mgf--ambar {
  --cap-cor: var(--cor-ambar);
}

.mgf--marca {
  --cap-cor: var(--cor-marca);
}

.mgf__topo {
  display: flex;
  align-items: center;
  padding: var(--esp-3, 12px) var(--esp-4, 16px);
  border-bottom: 1px solid rgb(var(--slate-4));
}

.mgf__tit {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-2, 8px);
  font-size: var(--txt-dado, 14px);
  font-weight: 600;
  color: rgb(var(--slate-12));
}

.mgf__tit > i {
  width: 16px;
  height: 16px;
  color: #8b7bff;
}

.mgf__conta {
  display: grid;
  place-items: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #8b7bff;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.mgf__lista {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: min(440px, 64vh);
  margin: 0;
  padding: var(--esp-2, 8px);
  overflow-y: auto;
  list-style: none;
}

.mgf__item {
  display: flex;
  align-items: center;
  gap: var(--esp-3, 12px);
  width: 100%;
  min-height: 56px;
  padding: var(--esp-2, 8px) var(--esp-3, 12px);
  border: 0;
  border-radius: var(--r-3, 10px);
  background: transparent;
  text-align: start;
  cursor: pointer;
  transition: background var(--mov-1, 180ms) var(--mov-curva, ease);
}

.mgf__item:hover {
  background: rgb(var(--slate-3));
}

.mgf__item:focus-visible {
  outline: 2px solid rgb(var(--cap-cor));
  outline-offset: -2px;
}

.mgf__ico {
  display: grid;
  flex: none;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgb(var(--cap-cor) / 0.14);
}

.mgf__ico > i {
  width: 18px;
  height: 18px;
  color: rgb(var(--cap-cor));
}

.mgf__txt {
  display: grid;
  flex: 1;
  gap: 2px;
  min-width: 0;
}

.mgf__nome {
  overflow: hidden;
  color: rgb(var(--slate-12));
  font-size: var(--txt-corpo, 13.5px);
  font-weight: 600;
  line-height: 1.3;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.mgf__item--nova .mgf__nome {
  font-weight: 700;
}

.mgf__quando {
  overflow: hidden;
  color: rgb(var(--slate-11));
  font-size: var(--txt-nota, 12.5px);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mgf__ponto {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #8b7bff;
  box-shadow: 0 0 0 3px rgb(139 123 255 / 0.2);
}

.mgf__rodape {
  border-top: 1px solid rgb(var(--slate-4));
}

.mgf__ver {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  padding: var(--esp-3, 12px);
  border: 0;
  background: transparent;
  color: rgb(var(--blue-11));
  font-size: var(--txt-nota, 12.5px);
  font-weight: 600;
  cursor: pointer;
  transition: background var(--mov-1, 180ms) var(--mov-curva, ease);
}

.mgf__ver:hover {
  background: rgb(var(--blue-2));
}

.mgf__ver > i {
  width: 14px;
  height: 14px;
}

@media (prefers-reduced-motion: reduce) {
  .megafone--novo .megafone__ico,
  .megafone__ponto {
    animation: none;
  }
}
</style>
